import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import {
  CommentsApi,
  type NotificationGetNotificationsReadDto,
  type NotificationReadDto,
  OpenBriefsApi,
} from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { NOTIFICATIONS_PAGE_SIZE } from "@/constants/inbox.constants";
import { selectedClientServiceIdAtom } from "@/store";

type NotificationsQueryData = {
  data?: NotificationGetNotificationsReadDto;
};

const commentApiClient = createApiClient(CommentsApi);
const briefApiClient = createApiClient(OpenBriefsApi);

export const useComments = () => {
  const queryClient = useQueryClient();

  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const useAddComment = (params: {
    orderLogId?: string;
    briefId?: string;
    orderId?: string;
    orderItemId?: string;
    onNewNotificationCreated?: (
      notificationId: NotificationReadDto["id"]
    ) => void;
  }) =>
    useMutation({
      mutationFn: (body: { content: string; orderItemId?: string }) => {
        return commentApiClient.commentsControllerAddComment({
          clientServiceId: selectedClientServiceIdAtomValue,
          commentCreateCommand: {
            orderLogId: params.orderLogId,
            orderItemId: body.orderItemId,
            briefId: params.briefId,
            content: body.content,
          },
        });
      },
      onSettled: () =>
        Promise.all([
          params.orderLogId &&
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
          params.briefId &&
            queryClient.invalidateQueries({
              queryKey: ["briefs", params.briefId],
            }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
        ]),
      onSuccess: async () => {
        await Promise.all([
          params.orderLogId &&
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
          queryClient.refetchQueries({ queryKey: ["notifications"] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
        ]);

        if (params.onNewNotificationCreated) {
          const notificationsQueryKey = [
            "notifications",
            1,
            NOTIFICATIONS_PAGE_SIZE,
            undefined,
            selectedClientServiceIdAtomValue,
          ];

          const notificationsData =
            queryClient.getQueryData<NotificationsQueryData>(
              notificationsQueryKey
            );
          const notifications = notificationsData?.data?.data;

          if (notifications?.length) {
            const latestNotification = [...notifications].sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )[0];

            params.onNewNotificationCreated(latestNotification.id);
          }
        }
      },
    });

  const useGetPaginatedBriefComments = (briefId: string) =>
    useQuery({
      queryKey: ["briefs", briefId, "comments"],
      queryFn: () =>
        briefApiClient.openBriefsControllerGetComments({
          briefId,
          clientServiceId: selectedClientServiceIdAtomValue,
          nbPerPage: 999,
        }),
      enabled: !!briefId && !!selectedClientServiceIdAtomValue,
    });

  return {
    useAddComment,
    useGetPaginatedBriefComments,
  };
};
